import { CLink } from '@/CLink';
import { ReactComponent as NavChevronIcon } from '@a/icons/right-chevron.svg';
import { SerializedStyles, css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode } from '@mdx-js/react/lib';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import useLocationData from '@s/hooks/useLocationData';
import { navBtnStyles, navLinkStyles, subNavLinkStyles } from './NavLinkStyles';
import { NavLinksTypes } from './NavLinks';
import { flexCenter } from './styles/classes';

const MenuList = styled(NavigationMenu.List)`
    ${flexCenter};
    justify-content: flex-start;
    list-style-type: none;
    padding-inline-start: 0;
    margin: 0;

    > li {
        position: relative; // for submenu align with item
    }
`;

const Content = styled(NavigationMenu.Content)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    animation-duration: 300ms;
    animation-timing-function: ease;

    > ul {
        list-style-type: none;
        padding-inline-start: 0;
        margin: 0;
        max-width: 1440px;
    }

    &[data-state='open'],
    &[data-motion='from-start'] {
        animation-name: enterFromBottom;
    }

    &[data-motion='from-end'] {
        animation-name: enterFromBottom;
    }

    &[data-state='closed'],
    &[data-motion='to-start'] {
        animation-name: exitToBottom;
    }
    &[data-motion='to-end'] {
        animation-name: exitToBottom;
    }

    @keyframes enterFromBottom {
        from {
            opacity: 0;
            transform: translateY(50px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes exitToBottom {
        from {
            opacity: 1;
            transform: translateY(0);
        }
        to {
            opacity: 0;
            transform: translateY(50px);
        }
    }
`;

type MenuProps = {
    children?: ReactNode;
    menuStyles?: SerializedStyles;
    contentStyles: SerializedStyles;
    links: NavLinksTypes;
    className?: string;
};

export const DesktopNav = ({
    children,
    menuStyles,
    contentStyles,
    links,
    className,
}: MenuProps) => {
    const { title, category } = useLocationData();

    return (
        <NavigationMenu.Root className={className}>
            <MenuList css={menuStyles}>
                {links.map((data, i) =>
                    data.links ? (
                        <NavigationMenu.Item key={i}>
                            <NavigationMenu.Trigger
                                css={navBtnStyles}
                                className={
                                    category === data.text || title === data.text
                                        ? 'current-nav-category'
                                        : ''
                                }
                            >
                                {data.text}
                                <NavChevronIcon />
                            </NavigationMenu.Trigger>
                            <Content css={contentStyles} className="desktop-nav-dropdown">
                                <ul>
                                    {data.links?.map((subData, i) =>
                                        subData.text.includes('(coming soon)') ? (
                                            <NavigationMenu.Item key={i}>
                                                <p
                                                    css={theme => css`
                                                        ${subNavLinkStyles}
                                                        color: ${theme.colors.gray3} !important;
                                                        pointer-events: none !important;
                                                    `}
                                                >
                                                    {subData.text}
                                                </p>
                                            </NavigationMenu.Item>
                                        ) : (
                                            <NavigationMenu.Item key={i}>
                                                <NavigationMenu.Link
                                                    asChild
                                                    active={
                                                        subData.text.toLowerCase() ===
                                                        title.toLowerCase()
                                                    }
                                                >
                                                    <CLink to={subData.link} css={subNavLinkStyles}>
                                                        {subData.text}
                                                    </CLink>
                                                </NavigationMenu.Link>
                                            </NavigationMenu.Item>
                                        )
                                    )}
                                </ul>
                            </Content>
                        </NavigationMenu.Item>
                    ) : data.text.includes('(coming soon)') ? (
                        <NavigationMenu.Item key={i}>
                            <p
                                css={theme => css`
                                    ${navLinkStyles}
                                    color: ${theme.colors.gray3};
                                    margin-top: 0;

                                    ::before {
                                        content: none;
                                    }
                                `}
                            >
                                {data.text}
                            </p>
                        </NavigationMenu.Item>
                    ) : (
                        <NavigationMenu.Item key={i}>
                            <NavigationMenu.Link asChild active={data.text === title}>
                                <CLink to={data.link} css={navLinkStyles}>
                                    {data.text}
                                </CLink>
                            </NavigationMenu.Link>
                        </NavigationMenu.Item>
                    )
                )}
                {children}
            </MenuList>
        </NavigationMenu.Root>
    );
};

export const NavItem = ({ children, ...props }: { children: ReactNode; id?: string }) => (
    <NavigationMenu.Item {...props}>
        <NavigationMenu.Link asChild>{children}</NavigationMenu.Link>
    </NavigationMenu.Item>
);
